import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import dw from './daywiseItinerary.module.css';

const ROOM_TYPES = ['Single', 'Double', 'Triple', 'Quad', 'Family'];

const emptyRoomConfig = (roomType) => ({
  roomType,
  adultRateShared: 0,
  adultRateIndian: 0,
  adultRateForeign: 0,
  childBedRateShared: 0,
  childBedRateIndian: 0,
  childBedRateForeign: 0,
  childNoBedRateShared: 0,
  childNoBedRateIndian: 0,
  childNoBedRateForeign: 0,
  beds: roomType === 'Single' ? 1 : 2,
  maxAdults: roomType === 'Single' ? 1 : 2,
  maxChildNoBed: 1,
  maxTotal: roomType === 'Single' ? 2 : 4,
});

const emptyHotel = (city) => ({
  city: city || '',
  starRating: 3,
  hotelName: '',
  pricingMethod: 'room',
  nationalityPricing: 'separate',
  roomConfigs: [emptyRoomConfig('Double')],
});

const HotelCatalogue = ({ hotels, cities, onChange }) => {
  const cityList = useMemo(() => {
    const fromDays = cities || [];
    const fromHotels = (hotels || []).map((h) => h.city).filter(Boolean);
    return [...new Set([...fromDays, ...fromHotels])];
  }, [cities, hotels]);

  const [selectedCity, setSelectedCity] = useState(cityList[0] || '');
  const [manualCity, setManualCity] = useState('');

  useEffect(() => {
    if (cityList.length === 0) return;
    if (!selectedCity || !cityList.includes(selectedCity)) {
      setSelectedCity(cityList[0]);
    }
  }, [cityList, selectedCity]);

  const effectiveCity = selectedCity || manualCity.trim();

  const [starFilter, setStarFilter] = useState(0);
  const [selectedGlobalIdx, setSelectedGlobalIdx] = useState(-1);
  const [roomTab, setRoomTab] = useState('Double');

  const hotelList = hotels || [];

  const visibleHotels = useMemo(
    () =>
      hotelList
        .map((h, i) => ({ hotel: h, globalIdx: i }))
        .filter(
          ({ hotel: h }) =>
            (h.city || '') === (selectedCity || '') &&
            (starFilter === 0 || h.starRating === starFilter)
        ),
    [hotelList, selectedCity, starFilter]
  );

  const selectedHotel =
    selectedGlobalIdx >= 0 && hotelList[selectedGlobalIdx] ? hotelList[selectedGlobalIdx] : null;

  useEffect(() => {
    if (selectedGlobalIdx < 0) {
      if (visibleHotels.length > 0) {
        setSelectedGlobalIdx(visibleHotels[0].globalIdx);
      }
      return;
    }
    const stillVisible = visibleHotels.some((e) => e.globalIdx === selectedGlobalIdx);
    if (!stillVisible) {
      setSelectedGlobalIdx(visibleHotels[0]?.globalIdx ?? -1);
    }
  }, [visibleHotels, selectedGlobalIdx]);

  const updateHotels = (next) => onChange(next);

  const patchHotel = (patch) => {
    if (selectedGlobalIdx < 0) return;
    const next = [...hotelList];
    next[selectedGlobalIdx] = { ...next[selectedGlobalIdx], ...patch };
    updateHotels(next);
  };

  const patchRoomConfig = (roomType, patch) => {
    if (selectedGlobalIdx < 0) return;
    const current = hotelList[selectedGlobalIdx];
    if (!current) return;
    const configs = [...(current.roomConfigs || [])];
    let rIdx = configs.findIndex((r) => r.roomType === roomType);
    if (rIdx < 0) {
      configs.push(emptyRoomConfig(roomType));
      rIdx = configs.length - 1;
    }
    configs[rIdx] = { ...configs[rIdx], ...patch };
    const next = [...hotelList];
    next[selectedGlobalIdx] = { ...next[selectedGlobalIdx], roomConfigs: configs };
    updateHotels(next);
  };

  const addHotel = () => {
    const city = (selectedCity || manualCity.trim()).trim();
    if (!city) return;
    const next = [...hotelList, emptyHotel(city)];
    updateHotels(next);
    if (!selectedCity) {
      setSelectedCity(city);
    }
    setSelectedGlobalIdx(next.length - 1);
  };

  const roomCfg =
    selectedHotel?.roomConfigs?.find((r) => r.roomType === roomTab) ||
    emptyRoomConfig(roomTab);

  return (
    <div className={dw.catalogueRoot}>
      <h4>Hotels</h4>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {cityList.map((c) => (
          <Button
            key={c}
            size="sm"
            variant={selectedCity === c ? 'primary' : 'outline-secondary'}
            onClick={() => {
              setSelectedCity(c);
              const firstIdx = hotelList.findIndex((h) => h.city === c);
              setSelectedGlobalIdx(firstIdx);
            }}
          >
            {c || '(no city)'}
          </Button>
        ))}
        {cityList.length === 0 && (
          <div className={dw.field} style={{ minWidth: 200 }}>
            <Form.Label className={dw.fieldLabel}>Hotel city</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Enter city for new hotel"
              value={manualCity}
              onChange={(e) => setManualCity(e.target.value)}
            />
            <small className="text-muted">
              Or set overnight city on a day tab — it appears here automatically.
            </small>
          </div>
        )}
      </div>

      <Row className="mb-2">
        <Col md={3}>
          <Form.Label>Star filter</Form.Label>
          <Form.Control
            as="select"
            value={starFilter}
            onChange={(e) => setStarFilter(Number(e.target.value))}
          >
            <option value={0}>All</option>
            {[1, 2, 3, 4, 5].map((s) => (
              <option key={s} value={s}>
                {s}★
              </option>
            ))}
          </Form.Control>
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button onClick={addHotel} disabled={!effectiveCity}>
            Add hotel
          </Button>
        </Col>
      </Row>

      <div className={dw.hotelListWrap}>
        <Table bordered size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Stars</th>
              <th>Pricing</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visibleHotels.map(({ hotel: h, globalIdx }) => (
              <tr
                key={globalIdx}
                role="button"
                tabIndex={0}
                style={{
                  background: globalIdx === selectedGlobalIdx ? '#eef6ff' : undefined,
                  cursor: 'pointer',
                }}
                onClick={() => setSelectedGlobalIdx(globalIdx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedGlobalIdx(globalIdx);
                  }
                }}
              >
                <td>{h.hotelName || '(unnamed)'}</td>
                <td>{h.starRating}★</td>
                <td>{h.pricingMethod}</td>
                <td>
                  <Button
                    size="sm"
                    variant="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedGlobalIdx(globalIdx);
                    }}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
            {visibleHotels.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted text-center">
                  No hotels for this city
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {selectedHotel && (
        <div style={{ border: '1px solid #ddd', padding: 12 }}>
          <Row className="mb-2">
            <Col md={6}>
              <Form.Label>Hotel name</Form.Label>
              <Form.Control
                value={selectedHotel.hotelName || ''}
                onChange={(e) => patchHotel({ hotelName: e.target.value })}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Stars</Form.Label>
              <Form.Control
                as="select"
                value={selectedHotel.starRating ?? 3}
                onChange={(e) => patchHotel({ starRating: Number(e.target.value) })}
              >
                {[1, 2, 3, 4, 5].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={2}>
              <Form.Label>Method</Form.Label>
              <Form.Control
                as="select"
                value={selectedHotel.pricingMethod || 'room'}
                onChange={(e) => patchHotel({ pricingMethod: e.target.value })}
              >
                <option value="room">Per room</option>
                <option value="person">Per person</option>
              </Form.Control>
            </Col>
            <Col md={2}>
              <Form.Label>Nat. pricing</Form.Label>
              <Form.Control
                as="select"
                value={selectedHotel.nationalityPricing || 'separate'}
                onChange={(e) => patchHotel({ nationalityPricing: e.target.value })}
              >
                <option value="shared">Shared</option>
                <option value="separate">Separate</option>
              </Form.Control>
            </Col>
          </Row>

          <div className={dw.tabline}>
            {ROOM_TYPES.map((rt) => (
              <Button
                key={rt}
                size="sm"
                variant={roomTab === rt ? 'primary' : 'outline-secondary'}
                onClick={() => setRoomTab(rt)}
              >
                {rt}
              </Button>
            ))}
          </div>

          <Row>
            <Col md={3}>
              <Form.Label>Adult (shared)</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.adultRateShared ?? 0}
                onChange={(e) =>
                  patchRoomConfig(roomTab, { adultRateShared: Number(e.target.value) })
                }
              />
            </Col>
            <Col md={3}>
              <Form.Label>Adult IN</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.adultRateIndian ?? 0}
                onChange={(e) =>
                  patchRoomConfig(roomTab, { adultRateIndian: Number(e.target.value) })
                }
              />
            </Col>
            <Col md={3}>
              <Form.Label>Adult FN</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.adultRateForeign ?? 0}
                onChange={(e) =>
                  patchRoomConfig(roomTab, { adultRateForeign: Number(e.target.value) })
                }
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={3}>
              <Form.Label>Child bed (shared)</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.childBedRateShared ?? 0}
                onChange={(e) =>
                  patchRoomConfig(roomTab, { childBedRateShared: Number(e.target.value) })
                }
              />
            </Col>
            <Col md={3}>
              <Form.Label>Child no bed (shared)</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.childNoBedRateShared ?? 0}
                onChange={(e) =>
                  patchRoomConfig(roomTab, { childNoBedRateShared: Number(e.target.value) })
                }
              />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col md={2}>
              <Form.Label>Beds</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.beds ?? 1}
                onChange={(e) => patchRoomConfig(roomTab, { beds: Number(e.target.value) })}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Max adults</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.maxAdults ?? 1}
                onChange={(e) => patchRoomConfig(roomTab, { maxAdults: Number(e.target.value) })}
              />
            </Col>
            <Col md={2}>
              <Form.Label>Max child NB</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.maxChildNoBed ?? 0}
                onChange={(e) =>
                  patchRoomConfig(roomTab, { maxChildNoBed: Number(e.target.value) })
                }
              />
            </Col>
            <Col md={2}>
              <Form.Label>Max total</Form.Label>
              <Form.Control
                type="number"
                value={roomCfg.maxTotal ?? 2}
                onChange={(e) => patchRoomConfig(roomTab, { maxTotal: Number(e.target.value) })}
              />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default HotelCatalogue;
